import React from 'react'
import { BankingDetailsRequest } from '../../core/_models'
import {
  blurNumberInputOnWheel,
  preventNumberInputArrowStep,
} from '../../../../shared/utils/numberInputGuards'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

interface Props {
  data: BankingDetailsRequest
  onChange: (data: BankingDetailsRequest) => void
  fieldErrors: Record<string, string>
  onFieldError: (field: string, error: string) => void
}

const BankingDetails: React.FC<Props> = ({ data, onChange, fieldErrors, onFieldError }) => {
  const IFSC_RTGS_LENGTH = 11
  const IBAN_MIN_LENGTH = 15
  const IBAN_MAX_LENGTH = 34
  const currencyOptions = ['USD', 'EUR', 'GBP', 'SGD', 'INR', 'AED', 'OTHER']
  const bankCodeOptions: Array<{ value: 'IFSC' | 'RTGS'; label: string }> = [
    { value: 'RTGS', label: 'IFSC / RTGS' },
    { value: 'IFSC', label: 'SWIFT / IBAN' },
  ]
  const salaryFields: Array<keyof BankingDetailsRequest> = [
    'basicWages',
    'fixedOvertime109Hrs',
    'leaveWages9DaysPerMonth',
    'subSmb',
    'tradeAllowance',
    'otherAllowance',
    'ownersLoyaltyBonus',
    'rejoiningBonus',
  ]
  const salaryBreakdownFields: Array<keyof BankingDetailsRequest> = [
    'basicWages',
    'fixedOvertime109Hrs',
    'leaveWages9DaysPerMonth',
    'subSmb',
    'tradeAllowance',
    'otherAllowance',
    'ownersLoyaltyBonus',
    'rejoiningBonus',
  ]

  const toAmount = (value: unknown): number => {
    const n = Number(value)
    return Number.isFinite(n) ? n : 0
  }

  const parseAmountInput = (value: string): number | null => {
    const trimmed = value.trim()
    if (!trimmed) return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }

  const applyDeductionAggregate = (nextData: BankingDetailsRequest) => {
    const others = toAmount(nextData.otherDeductions)
    const cashAdvance = toAmount(nextData.cashAdvanceFromOffice)
    nextData.deductionsTotal = cashAdvance + others
  }

  const formatAmount = (value: number) =>
    value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const amountInputProps = {
    type: 'number' as const,
    min: 0,
    step: '0.01',
    onWheel: blurNumberInputOnWheel,
    onKeyDown: preventNumberInputArrowStep,
  }

  const handleChange = (field: keyof BankingDetailsRequest, value: any) => {
    const nextData: BankingDetailsRequest = {
      ...data,
      [field]: value,
    }

    if (field === 'ifscRtgsType') {
      if (value === 'RTGS') {
        nextData.swiftIandi = null
      } else {
        nextData.ifscRtgsCode = null
      }
      onFieldError('ifscRtgsCode', '')
      onFieldError('swiftIandi', '')
    }

    if (field === 'salaryCurrency' && value !== 'OTHER') {
      nextData.salaryOtherCurrency = null
      onFieldError('salaryOtherCurrency', '')
    }

    if (field === 'accountCurrency' && value !== 'OTHER') {
      nextData.otherCurrency = null
      onFieldError('otherCurrency', '')
    }

    if (salaryFields.includes(field)) {
      const hasBreakdownValue = salaryBreakdownFields.some(
        (key) => nextData[key] !== null && nextData[key] !== undefined
      )
      nextData.salary = hasBreakdownValue
        ? salaryBreakdownFields.reduce((sum, key) => sum + toAmount(nextData[key]), 0)
        : null
    }

    if (field === 'cashAdvanceFromOffice' || field === 'otherDeductions') {
      applyDeductionAggregate(nextData)
    }

    onChange(nextData)

    // Clear error when field is filled
    if (value !== null && value !== undefined && value.toString().trim()) {
      onFieldError(field, '')
    }
  }

  const validateField = (field: keyof BankingDetailsRequest, value: any) => {
    const fieldLabels: Record<string, string> = {
      nameOfBeneficiary: 'Name of Beneficiary',
      accountNumber: 'Account Number',
      bankName: 'Bank Name',
      branchName: 'Branch Name',
      branchCode: 'Branch Code',
      ifscRtgsCode: 'IFSC / RTGS Code',
      swiftIandi: 'SWIFT / IBAN Code',
      bankAddress: 'Bank Address',
      accountCurrency: 'Account Currency',
      salaryCurrency: 'Salary Currency',
      ifscRtgsType: 'Bank Code',
      basicWages: 'Basic',
      fixedOvertime109Hrs: 'Fixed Overtime (109 Hrs)',
      leaveWages9DaysPerMonth: 'Leave Wages',
      subSmb: 'Sub / SMB',
      tradeAllowance: 'Trade Allowance',
      otherAllowance: 'Other Allowance',
      ownersLoyaltyBonus: "Owner's / Loyalty Bonus",
      rejoiningBonus: 'Re-joining Bonus',
      overtimeRate: 'Overtime Rate',
    }

    const requiredFields = [
      'nameOfBeneficiary',
      'accountNumber',
      'bankName',
      'branchName',
      'branchCode',
      'ifscRtgsType',
      'bankAddress',
      'salaryCurrency',
      'basicWages',
      'leaveWages9DaysPerMonth',
      'otherAllowance',
      'overtimeRate',
    ]

    if (requiredFields.includes(field)) {
      const isMissing =
        value === null || value === undefined || (typeof value === 'string' && !value.trim())

      if (isMissing) {
        const label = fieldLabels[field as string] || field
        onFieldError(field, `${label} is required`)
      }
    }

    // Account number validation - 8-20 alphanumeric characters
    if (field === 'accountNumber') {
      if (value && !/^[a-zA-Z0-9]{8,20}$/.test(value)) {
        onFieldError(field, 'Account Number must be 8-20 alphanumeric characters')
      }
    }

    // SWIFT code validation - 8 or 11 characters
    if (field === 'swiftIandi' && data.ifscRtgsType !== 'RTGS') {
      const normalized = value ? String(value).replace(/\s+/g, '').toUpperCase() : ''
      const isSwift = /^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(normalized)
      const isIban = new RegExp(`^[A-Z0-9]{${IBAN_MIN_LENGTH},${IBAN_MAX_LENGTH}}$`).test(
        normalized
      )
      if (value && !(isSwift || isIban)) {
        onFieldError(
          field,
          `SWIFT / IBAN Code must be 8 or 11 characters (SWIFT) or ${IBAN_MIN_LENGTH}-${IBAN_MAX_LENGTH} characters (IBAN)`
        )
      }
    }

    // Branch code validation - 2-10 alphanumeric or hyphen
    if (field === 'branchCode') {
      if (value && !/^[a-zA-Z0-9\-]{2,10}$/.test(value)) {
        onFieldError(field, 'Branch Code must be 2-10 alphanumeric characters or hyphens')
      }
    }

    // IFSC/RTGS code validation
    if (field === 'ifscRtgsCode') {
      if (data.ifscRtgsType === 'RTGS') {
        const normalized = value ? String(value).replace(/\s+/g, '').toUpperCase() : ''
        const isIfscRtgs = new RegExp(`^[A-Z0-9]{${IFSC_RTGS_LENGTH}}$`).test(normalized)
        if (value && !isIfscRtgs) {
          onFieldError(
            field,
            `IFSC / RTGS Code must be ${IFSC_RTGS_LENGTH} alphanumeric characters`
          )
        }
      }
    }

    // Conditional required bank code field
    if (field === 'ifscRtgsType') {
      if (value === 'RTGS') {
        if (!data.ifscRtgsCode || !data.ifscRtgsCode.toString().trim()) {
          onFieldError('ifscRtgsCode', 'IFSC / RTGS Code is required')
        }
      } else {
        if (!data.swiftIandi || !data.swiftIandi.toString().trim()) {
          onFieldError('swiftIandi', 'SWIFT / IBAN Code is required')
        }
      }
    }

    if (field === 'ifscRtgsCode' && data.ifscRtgsType === 'RTGS') {
      if (!value || !value.toString().trim()) {
        onFieldError('ifscRtgsCode', 'IFSC / RTGS Code is required')
      }
    }

    if (field === 'swiftIandi' && data.ifscRtgsType !== 'RTGS') {
      if (!value || !value.toString().trim()) {
        onFieldError('swiftIandi', 'SWIFT / IBAN Code is required')
      }
    }

    // Other currency validation when selected
    if (field === 'salaryOtherCurrency' && data.salaryCurrency === 'OTHER') {
      if (!value || !value.toString().trim()) {
        onFieldError(field, 'Currency Code is required')
      } else if (!/^[A-Z]{3}$/.test(value)) {
        onFieldError(field, 'Currency Code must be 3 uppercase letters')
      }
    }

    if (field === 'otherCurrency' && data.accountCurrency === 'OTHER') {
      if (!value || !value.toString().trim()) {
        onFieldError(field, 'Currency Code is required')
      } else if (!/^[A-Z]{3}$/.test(value)) {
        onFieldError(field, 'Currency Code must be 3 uppercase letters')
      }
    }
  }

  const breakdownTotal = salaryBreakdownFields.reduce((sum, key) => sum + toAmount(data[key]), 0)

  const hasAnyBreakdown = salaryBreakdownFields.some(
    (key) => data[key] !== null && data[key] !== undefined
  )

  const enteredSalary =
    data.salary === null || data.salary === undefined ? null : toAmount(data.salary)
  const adjustment = enteredSalary === null ? null : enteredSalary - breakdownTotal
  const hasAdjustment = adjustment !== null && hasAnyBreakdown && Math.abs(adjustment) > 0.009

  const otherMiscDeduction = toAmount(data.otherDeductions)

  return (
    <div className='banking-details-form'>
      <div className='row'>
        {/* Name of Beneficiary */}
        <div className='col-md-6 mb-3'>
          <label className='modal_label'>
            Name of Beneficiary <span className='text-danger'>*</span>
          </label>
          <input
            type='text'
            className={`form-control ${fieldErrors.nameOfBeneficiary ? 'is-invalid' : ''}`}
            placeholder='Full name of account beneficiary'
            value={data.nameOfBeneficiary || ''}
            onChange={(e) => handleChange('nameOfBeneficiary', e.target.value)}
            onBlur={(e) => validateField('nameOfBeneficiary', e.target.value)}
          />
          {fieldErrors.nameOfBeneficiary && (
            <small className='text-danger d-block mt-1'>{fieldErrors.nameOfBeneficiary}</small>
          )}
        </div>

        {/* Account Number */}
        <div className='col-md-6 mb-3'>
          <label className='modal_label'>
            Account Number <span className='text-danger'>*</span>
          </label>
          <input
            type='text'
            className={`form-control ${fieldErrors.accountNumber ? 'is-invalid' : ''}`}
            placeholder='e.g., 12345678901234'
            value={data.accountNumber || ''}
            onChange={(e) => handleChange('accountNumber', e.target.value)}
            onBlur={(e) => validateField('accountNumber', e.target.value)}
            maxLength={20}
          />
          {fieldErrors.accountNumber && (
            <small className='text-danger d-block mt-1'>{fieldErrors.accountNumber}</small>
          )}
        </div>

        {/* Bank Name */}
        <div className='col-md-6 mb-3'>
          <label className='modal_label'>
            Bank Name <span className='text-danger'>*</span>
          </label>
          <input
            type='text'
            className={`form-control ${fieldErrors.bankName ? 'is-invalid' : ''}`}
            placeholder='e.g., HDFC Bank, Standard Chartered'
            value={data.bankName || ''}
            onChange={(e) => handleChange('bankName', e.target.value)}
            onBlur={(e) => validateField('bankName', e.target.value)}
          />
          {fieldErrors.bankName && (
            <small className='text-danger d-block mt-1'>{fieldErrors.bankName}</small>
          )}
        </div>

        {/* Branch Name */}
        <div className='col-md-6 mb-3'>
          <label className='modal_label'>
            Branch Name <span className='text-danger'>*</span>
          </label>
          <input
            type='text'
            className={`form-control ${fieldErrors.branchName ? 'is-invalid' : ''}`}
            placeholder='e.g., Mumbai Main, Singapore Branch'
            value={data.branchName || ''}
            onChange={(e) => handleChange('branchName', e.target.value)}
            onBlur={(e) => validateField('branchName', e.target.value)}
          />
          {fieldErrors.branchName && (
            <small className='text-danger d-block mt-1'>{fieldErrors.branchName}</small>
          )}
        </div>

        {/* Branch Code */}
        <div className='col-md-6 mb-3'>
          <label className='modal_label'>
            Branch Code <span className='text-danger'>*</span>
          </label>
          <input
            type='text'
            className={`form-control ${fieldErrors.branchCode ? 'is-invalid' : ''}`}
            placeholder='e.g., 0001 or BR-001'
            value={data.branchCode || ''}
            onChange={(e) => handleChange('branchCode', e.target.value)}
            onBlur={(e) => validateField('branchCode', e.target.value)}
            maxLength={10}
          />
          {fieldErrors.branchCode && (
            <small className='text-danger d-block mt-1'>{fieldErrors.branchCode}</small>
          )}
        </div>

        {/* Bank Code Dropdown */}
        <div className='col-md-6 mb-3'>
          <label className='modal_label'>
            Bank Code <span className='text-danger'>*</span>
          </label>
          <SearchableSelect
            className={`form-select ${fieldErrors.ifscRtgsType ? 'is-invalid' : ''}`}
            value={data.ifscRtgsType || 'IFSC'}
            onChange={(e) => handleChange('ifscRtgsType', e.target.value as 'IFSC' | 'RTGS')}
            onBlur={(e) => validateField('ifscRtgsType', e?.target?.value || data.ifscRtgsType)}
          >
            <option value=''>--Select Bank Code--</option>
            {bankCodeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SearchableSelect>
          {fieldErrors.ifscRtgsType && (
            <small className='text-danger d-block mt-1'>{fieldErrors.ifscRtgsType}</small>
          )}
        </div>

        {/* Conditional Bank Code Input */}
        {data.ifscRtgsType === 'RTGS' ? (
          <div className='col-md-6 mb-3'>
            <label className='modal_label'>
              IFSC / RTGS Code <span className='text-danger'>*</span>
            </label>
            <input
              type='text'
              className={`form-control ${fieldErrors.ifscRtgsCode ? 'is-invalid' : ''}`}
              placeholder='e.g., HDFC0123456'
              value={data.ifscRtgsCode || ''}
              onChange={(e) =>
                handleChange('ifscRtgsCode', e.target.value.replace(/\s+/g, '').toUpperCase())
              }
              onBlur={(e) => validateField('ifscRtgsCode', e.target.value)}
              maxLength={IFSC_RTGS_LENGTH}
            />
            {fieldErrors.ifscRtgsCode && (
              <small className='text-danger d-block mt-1'>{fieldErrors.ifscRtgsCode}</small>
            )}
            <small className='text-muted d-block mt-1'>IFSC/RTGS: 11 characters</small>
          </div>
        ) : (
          <div className='col-md-6 mb-3'>
            <label className='modal_label'>
              SWIFT / IBAN Code <span className='text-danger'>*</span>
            </label>
            <input
              type='text'
              className={`form-control ${fieldErrors.swiftIandi ? 'is-invalid' : ''}`}
              placeholder='e.g., HDFCINBB (SWIFT) or DE89370400440532013000 (IBAN)'
              value={data.swiftIandi || ''}
              onChange={(e) =>
                handleChange('swiftIandi', e.target.value.replace(/\s+/g, '').toUpperCase())
              }
              onBlur={(e) => validateField('swiftIandi', e.target.value)}
              maxLength={IBAN_MAX_LENGTH}
            />
            {fieldErrors.swiftIandi && (
              <small className='text-danger d-block mt-1'>{fieldErrors.swiftIandi}</small>
            )}
            <small className='text-muted d-block mt-1'>
              SWIFT: 8 or 11 characters, IBAN: 15-34 characters
            </small>
          </div>
        )}

        {/* Bank Address */}
        <div className='col-md-6 mb-3'>
          <label className='modal_label'>
            Bank Address <span className='text-danger'>*</span>
          </label>
          <textarea
            className={`form-control ${fieldErrors.bankAddress ? 'is-invalid' : ''}`}
            placeholder='Full address of the bank branch'
            rows={2}
            value={data.bankAddress || ''}
            onChange={(e) => handleChange('bankAddress', e.target.value)}
            onBlur={(e) => validateField('bankAddress', e.target.value)}
          />
          {fieldErrors.bankAddress && (
            <small className='text-danger d-block mt-1'>{fieldErrors.bankAddress}</small>
          )}
        </div>

        <div className='col-md-6 mb-3'>
          <label className='modal_label'>Account Currency</label>
          <SearchableSelect
            className={`form-select ${fieldErrors.accountCurrency ? 'is-invalid' : ''}`}
            value={data.accountCurrency || ''}
            onChange={(e) => handleChange('accountCurrency', e.target.value)}
            onBlur={(e) => validateField('accountCurrency', e?.target?.value || data.accountCurrency)}
          >
            <option value=''>--Select Currency--</option>
            {currencyOptions.map((currency) => (
              <option key={currency} value={currency}>
                {currency === 'OTHER' ? 'Others (Specify)' : currency}
              </option>
            ))}
          </SearchableSelect>
          {fieldErrors.accountCurrency && (
            <small className='text-danger d-block mt-1'>{fieldErrors.accountCurrency}</small>
          )}
        </div>

        {data.accountCurrency === 'OTHER' && (
          <div className='col-md-6 mb-3'>
            <label className='modal_label'>
              Specify Currency <span className='text-danger'>*</span>
            </label>
            <input
              type='text'
              className={`form-control ${fieldErrors.otherCurrency ? 'is-invalid' : ''}`}
              placeholder='e.g., AUD, CAD, CHF'
              value={data.otherCurrency || ''}
              onChange={(e) => handleChange('otherCurrency', e.target.value.toUpperCase())}
              onBlur={(e) => validateField('otherCurrency', e.target.value.toUpperCase())}
              maxLength={3}
            />
            {fieldErrors.otherCurrency && (
              <small className='text-danger d-block mt-1'>{fieldErrors.otherCurrency}</small>
            )}
          </div>
        )}

        {/* Remarks */}
        <div className='col-12 mb-3'>
          <label className='modal_label'>Remarks</label>
          <textarea
            className='form-control'
            placeholder='Enter any additional remarks here'
            rows={3}
            value={data.remarks || ''}
            onChange={(e) => handleChange('remarks', e.target.value)}
          />
          <small className='text-muted d-block mt-1'></small>
        </div>

        <div className='col-12 mb-2'>
          <label className='modal_label fw-semibold'>Salary Breakdown</label>
        </div>

        <div className='col-md-6 mb-3'>
          <label className='modal_label'>
            Salary Currency <span className='text-danger'>*</span>
          </label>
          <SearchableSelect
            className={`form-select ${fieldErrors.salaryCurrency ? 'is-invalid' : ''}`}
            value={data.salaryCurrency || ''}
            onChange={(e) => handleChange('salaryCurrency', e.target.value)}
            onBlur={(e) => validateField('salaryCurrency', e?.target?.value || data.salaryCurrency)}
          >
            <option value=''>--Select Currency--</option>
            {currencyOptions.map((currency) => (
              <option key={currency} value={currency}>
                {currency === 'OTHER' ? 'Others (Specify)' : currency}
              </option>
            ))}
          </SearchableSelect>
          {fieldErrors.salaryCurrency && (
            <small className='text-danger d-block mt-1'>{fieldErrors.salaryCurrency}</small>
          )}
        </div>

        {data.salaryCurrency === 'OTHER' && (
          <div className='col-md-6 mb-3'>
            <label className='modal_label'>
              Specify Salary Currency <span className='text-danger'>*</span>
            </label>
            <input
              type='text'
              className={`form-control ${fieldErrors.salaryOtherCurrency ? 'is-invalid' : ''}`}
              placeholder='e.g., AUD, CAD, CHF'
              value={data.salaryOtherCurrency || ''}
              onChange={(e) => handleChange('salaryOtherCurrency', e.target.value.toUpperCase())}
              onBlur={(e) => validateField('salaryOtherCurrency', e.target.value.toUpperCase())}
              maxLength={3}
            />
            {fieldErrors.salaryOtherCurrency && (
              <small className='text-danger d-block mt-1'>{fieldErrors.salaryOtherCurrency}</small>
            )}
          </div>
        )}

        <div className='col-md-6 mb-3'>
          <label className='modal_label'>
            Basic <span className='text-danger'>*</span>
          </label>
          <input
            {...amountInputProps}
            className={`form-control ${fieldErrors.basicWages ? 'is-invalid' : ''}`}
            placeholder='Enter basic'
            value={data.basicWages ?? ''}
            onChange={(e) => handleChange('basicWages', parseAmountInput(e.target.value))}
            onBlur={(e) => validateField('basicWages', parseAmountInput(e.target.value))}
          />
          {fieldErrors.basicWages && (
            <small className='text-danger d-block mt-1'>{fieldErrors.basicWages}</small>
          )}
        </div>

        <div className='col-md-6 mb-3'>
          <label className='modal_label'>Fixed Overtime (109 Hrs)</label>
          <input
            {...amountInputProps}
            className={`form-control ${fieldErrors.fixedOvertime109Hrs ? 'is-invalid' : ''}`}
            placeholder='Enter fixed overtime'
            value={data.fixedOvertime109Hrs ?? ''}
            onChange={(e) => handleChange('fixedOvertime109Hrs', parseAmountInput(e.target.value))}
            onBlur={(e) => validateField('fixedOvertime109Hrs', parseAmountInput(e.target.value))}
          />
          {fieldErrors.fixedOvertime109Hrs && (
            <small className='text-danger d-block mt-1'>{fieldErrors.fixedOvertime109Hrs}</small>
          )}
        </div>

        <div className='col-md-6 mb-3'>
          <label className='modal_label'>
            Leave Wages <span className='text-danger'>*</span>
          </label>
          <input
            {...amountInputProps}
            className={`form-control ${fieldErrors.leaveWages9DaysPerMonth ? 'is-invalid' : ''}`}
            placeholder='Enter leave wages'
            value={data.leaveWages9DaysPerMonth ?? ''}
            onChange={(e) =>
              handleChange('leaveWages9DaysPerMonth', parseAmountInput(e.target.value))
            }
            onBlur={(e) =>
              validateField('leaveWages9DaysPerMonth', parseAmountInput(e.target.value))
            }
          />
          {fieldErrors.leaveWages9DaysPerMonth && (
            <small className='text-danger d-block mt-1'>
              {fieldErrors.leaveWages9DaysPerMonth}
            </small>
          )}
        </div>

        <div className='col-md-6 mb-3'>
          <label className='modal_label'>Sub / SMB</label>
          <input
            {...amountInputProps}
            className={`form-control ${fieldErrors.subSmb ? 'is-invalid' : ''}`}
            placeholder='Enter sub / smb'
            value={data.subSmb ?? ''}
            onChange={(e) => handleChange('subSmb', parseAmountInput(e.target.value))}
            onBlur={(e) => validateField('subSmb', parseAmountInput(e.target.value))}
          />
          {fieldErrors.subSmb && (
            <small className='text-danger d-block mt-1'>{fieldErrors.subSmb}</small>
          )}
        </div>

        <div className='col-md-6 mb-3'>
          <label className='modal_label'>Trade Allowance</label>
          <input
            {...amountInputProps}
            className={`form-control ${fieldErrors.tradeAllowance ? 'is-invalid' : ''}`}
            placeholder='Enter trade allowance'
            value={data.tradeAllowance ?? ''}
            onChange={(e) => handleChange('tradeAllowance', parseAmountInput(e.target.value))}
            onBlur={(e) => validateField('tradeAllowance', parseAmountInput(e.target.value))}
          />
          {fieldErrors.tradeAllowance && (
            <small className='text-danger d-block mt-1'>{fieldErrors.tradeAllowance}</small>
          )}
        </div>

        <div className='col-md-6 mb-3'>
          <label className='modal_label'>
            Other Allowance <span className='text-danger'>*</span>
          </label>
          <input
            {...amountInputProps}
            className={`form-control ${fieldErrors.otherAllowance ? 'is-invalid' : ''}`}
            placeholder='Enter other allowance'
            value={data.otherAllowance ?? ''}
            onChange={(e) => handleChange('otherAllowance', parseAmountInput(e.target.value))}
            onBlur={(e) => validateField('otherAllowance', parseAmountInput(e.target.value))}
          />
          {fieldErrors.otherAllowance && (
            <small className='text-danger d-block mt-1'>{fieldErrors.otherAllowance}</small>
          )}
        </div>

        <div className='col-md-6 mb-3'>
          <label className='modal_label'>Owner&apos;s / Loyalty Bonus</label>
          <input
            {...amountInputProps}
            className={`form-control ${fieldErrors.ownersLoyaltyBonus ? 'is-invalid' : ''}`}
            placeholder='Enter owner/loyalty bonus'
            value={data.ownersLoyaltyBonus ?? ''}
            onChange={(e) => handleChange('ownersLoyaltyBonus', parseAmountInput(e.target.value))}
            onBlur={(e) => validateField('ownersLoyaltyBonus', parseAmountInput(e.target.value))}
          />
          {fieldErrors.ownersLoyaltyBonus && (
            <small className='text-danger d-block mt-1'>{fieldErrors.ownersLoyaltyBonus}</small>
          )}
        </div>

        <div className='col-md-6 mb-3'>
          <label className='modal_label'>Re-joining Bonus</label>
          <input
            {...amountInputProps}
            className={`form-control ${fieldErrors.rejoiningBonus ? 'is-invalid' : ''}`}
            placeholder='Enter re-joining bonus'
            value={data.rejoiningBonus ?? ''}
            onChange={(e) => handleChange('rejoiningBonus', parseAmountInput(e.target.value))}
            onBlur={(e) => validateField('rejoiningBonus', parseAmountInput(e.target.value))}
          />
          {fieldErrors.rejoiningBonus && (
            <small className='text-danger d-block mt-1'>{fieldErrors.rejoiningBonus}</small>
          )}
        </div>

        <div className='col-md-6 mb-3'>
          <label className='modal_label'>
            Overtime Rate <span className='text-danger'>*</span>
          </label>
          <input
            {...amountInputProps}
            className={`form-control ${fieldErrors.overtimeRate ? 'is-invalid' : ''}`}
            placeholder='Enter overtime rate'
            value={data.overtimeRate ?? ''}
            onChange={(e) => handleChange('overtimeRate', parseAmountInput(e.target.value))}
            onBlur={(e) => validateField('overtimeRate', parseAmountInput(e.target.value))}
          />
          {fieldErrors.overtimeRate && (
            <small className='text-danger d-block mt-1'>{fieldErrors.overtimeRate}</small>
          )}
        </div>

        <div className='col-md-6 mb-3'>
          <label className='modal_label'>Total Monthly Wages</label>
          <input
            {...amountInputProps}
            className='form-control'
            placeholder='Enter total monthly wages'
            value={data.salary ?? ''}
            onChange={(e) => handleChange('salary', parseAmountInput(e.target.value))}
          />
          {hasAnyBreakdown && (
            <small className='text-muted d-block mt-1'>
              Calculated from breakdown: {formatAmount(breakdownTotal)}
            </small>
          )}
          {hasAdjustment && (
            <small className='text-warning d-block mt-1'>
              {adjustment! > 0
                ? `Extra added in total monthly wages: ${formatAmount(adjustment!)}`
                : `Manual reduction from breakdown total: ${formatAmount(Math.abs(adjustment!))}`}
            </small>
          )}
        </div>

        <div className='col-12 mb-2'>
          <label className='modal_label fw-semibold'>Deductions</label>
        </div>

        <div className='col-md-3 mb-3'>
          <label className='modal_label'>Union / Social dues</label>
          <input
            type='text'
            className='form-control form-control-solid'
            value={data.unionSocialDues || 'As per CBA'}
            readOnly
          />
        </div>

        <div className='col-md-3 mb-3'>
          <label className='modal_label'>Cash Advance from Office</label>
          <input
            {...amountInputProps}
            className='form-control'
            placeholder='0.00'
            value={data.cashAdvanceFromOffice ?? ''}
            onChange={(e) =>
              handleChange('cashAdvanceFromOffice', parseAmountInput(e.target.value))
            }
          />
        </div>

        <div className='col-md-3 mb-3'>
          <label className='modal_label'>Other Deductions</label>
          <input
            {...amountInputProps}
            className='form-control'
            placeholder='0.00'
            value={otherMiscDeduction || ''}
            onChange={(e) => handleChange('otherDeductions', parseAmountInput(e.target.value) ?? 0)}
          />
        </div>

        <div className='col-md-3 mb-3'>
          <label className='modal_label'>Total Deductions</label>
          <input
            type='number'
            className='form-control form-control-solid'
            value={data.deductionsTotal ?? 0}
            readOnly
          />
        </div>
      </div>
    </div>
  )
}

export default BankingDetails
